import {EScene} from '@/types/scene.types'
import {useGSAP} from '@gsap/react'
import {BackButton} from '@repo/shared/Customer/Coop/BackButton/BackButton'
import {Button} from '@repo/shared/Customer/Coop/Button/Button'
import {useTracking} from '@repo/shared/Tracking/Tracking.hooks'
import {getTrackingGameMode} from '@repo/shared/Tracking/Tracking.utils'
import {debugLogger} from '@repo/shared/utils/debugLogger'
import {isSinglePlayerMode} from '@repo/shared/utils/gameMode'
import clsx from 'clsx'
import gsap from 'gsap'
import {useAtomValue} from 'jotai'
import {FC, useEffect, useRef} from 'react'
import {TransitionStatus} from 'react-transition-state'
import {useReset} from '../Reset/Reset.hooks'
import {useStartSinglePlayerGame} from '../SinglePlayerMode/SinglePlayerMode.hooks'
import {gameModeState, sceneState, singlePlayerDataState} from '../Store/atoms'
interface ILobbySinglePlayerProps {
  transitionStatus: TransitionStatus
}

const LobbySinglePlayer: FC<ILobbySinglePlayerProps> = ({transitionStatus}) => {
  const scene = useAtomValue(sceneState)
  const gameMode = useAtomValue(gameModeState)
  const singlePlayerData = useAtomValue(singlePlayerDataState)

  const {startSinglePlayerGame} = useStartSinglePlayerGame()
  const {resetAll} = useReset()
  const {trackEvent} = useTracking()

  const rootEndingTimeLine = useRef(gsap.timeline({paused: true}))

  const enterTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))
  const exitTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))

  const containerRef = useRef<HTMLImageElement>(null)
  const logoRef = useRef<HTMLImageElement>(null)
  const dayTimeInfoRef = useRef<HTMLDivElement>(null)
  const dayTimePersonRef = useRef<HTMLDivElement>(null)
  const dayTimeRef = useRef<HTMLDivElement>(null)
  const startButtonRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      /**
       * Enter animation
       */
      enterTimeline.current.set(logoRef.current, {scale: 0, rotate: 3})
      enterTimeline.current.set(dayTimeInfoRef.current, {scale: 0})
      enterTimeline.current.set(dayTimePersonRef.current, {scale: 0})
      enterTimeline.current.set(dayTimeRef.current, {scale: 0, rotate: 3})
      enterTimeline.current.set(startButtonRef.current, {scale: 0})

      enterTimeline.current.to(containerRef.current, {opacity: 1, duration: 0.4})

      enterTimeline.current.to(
        logoRef.current,
        {
          scale: 1,
          rotate: 0,
          duration: 1,
          delay: 0.4,
          ease: 'elastic.out(1)',
        },
        '<',
      )

      enterTimeline.current.to(
        dayTimeInfoRef.current,
        {
          scale: 1,
          duration: 0.8,
          delay: 0.2,
          ease: 'elastic.out(1)',
        },
        '<',
      )

      enterTimeline.current.to(
        dayTimePersonRef.current,
        {
          scale: 1,
          duration: 0.8,
          delay: 0.2,
          ease: 'elastic.out(1)',
        },
        '<',
      )

      enterTimeline.current.to(
        dayTimeRef.current,
        {
          scale: 1,
          rotate: 0,
          duration: 0.8,
          delay: 0.2,
          ease: 'elastic.out(1)',
        },
        '<',
      )

      enterTimeline.current.to(
        startButtonRef.current,
        {
          scale: 1,
          duration: 0.8,
          delay: 0.2,
          ease: 'elastic.out(1)',
        },
        '<',
      )

      /**
       * Exit timeline
       */

      exitTimeline.current.to(containerRef.current, {
        opacity: 0,
        duration: 0.3,
      })
    },
    {
      dependencies: [scene],
    },
  )

  const onStart = () => {
    rootEndingTimeLine.current.play()
    startSinglePlayerGame()
  }

  /**
   * Enter and leave states
   */
  useEffect(() => {
    if (transitionStatus === 'entered' || transitionStatus === 'entering') {
      debugLogger({
        message: 'Enter state',
        tag: 'Lobby [single]',
      })
      enterTimeline.current.play()
    } else if (transitionStatus === 'exiting') {
      debugLogger({
        message: 'Exit state',
        tag: 'Lobby [single]',
      })
      enterTimeline.current.pause()
      exitTimeline.current.play()
    }
  }, [transitionStatus])

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 overflow-hidden bg-opacity-30 bg-[linear-gradient(to_bottom,#3e59e0CC,#160278CC)] opacity-0"
    >
      {!isSinglePlayerMode() && (
        <BackButton
          onClick={() => {
            trackEvent({
              domain: EScene.LOBBY,
              trigger: 'click',
              source: 'back_button',
              meta: {
                gameMode: getTrackingGameMode(gameMode),
              },
            })

            resetAll()
          }}
        />
      )}
      <div className="flex h-screen w-full flex-col items-center justify-center">
        <div id="daily-goal-info" className="flex flex-col">
          <div className="mx-[52px] mb-[48px] flex justify-center lg:mb-[64px]">
            <img ref={logoRef} className="w-[420px]" src="assets/logo.svg" />
          </div>
          <div className="m-auto flex w-fit flex-col items-center">
            <div
              id="beat-time-text"
              ref={dayTimeInfoRef}
              className="flex justify-center text-[20px] uppercase text-white lg:text-[24px]"
            >
              Slå dagens tid
            </div>

            {/* <div
              id="beat-name"
              ref={dayTimePersonRef}
              className="flex justify-center text-[24px] font-bold text-white lg:text-[30px]"
            >
              {singlePlayerData?.player}
            </div> */}

            <div
              id="beat-time"
              ref={dayTimeRef}
              className="my-[16px] flex w-fit items-center justify-center rounded-[16px] bg-white px-[32px] py-[8px] text-[48px] font-semibold tracking-normal text-[#071144] lg:my-[32px] lg:min-w-[220px] lg:text-[72px]"
            >
              {!singlePlayerData?.time ? '-' : singlePlayerData?.time}
            </div>
            <div ref={startButtonRef}>
              <Button
                disabled={!singlePlayerData?.time}
                id="start-btn"
                color="purple"
                size="small"
                className={clsx(
                  'w-full min-w-[172px] lg:min-w-[220px]',
                  transitionStatus === 'exiting' && '!pointer-events-none',
                )}
                onClick={() => {
                  trackEvent({
                    domain: EScene.LOBBY,
                    trigger: 'click',
                    source: 'start_button',
                    meta: {
                      gameMode: getTrackingGameMode(gameMode),
                    },
                  })

                  onStart()
                }}
              >
                start
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LobbySinglePlayer
